"use client";

import { useRef, useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";

/**
 * PageTransition — wraps page content with a fade + slide animation
 * that plays on route changes. Respects reduced-motion.
 *
 * On route change:
 *   1. Current content fades out + slides up slightly
 *   2. New content fades in + slides up from below
 *
 * Uses pathname change detection (no AnimatePresence needed).
 */
export default function PageTransition({ children }: { children: ReactNode }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const isFirst = useRef(true);

  useEffect(() => {
    if (!wrapRef.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Skip animation on initial mount (preloader handles that)
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    const el = wrapRef.current;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    // Scroll to top synchronously so users don't see old scroll position
    window.scrollTo({ top: 0 });

    // Dynamic-import GSAP — keeps it out of the critical bundle. The animation
    // runs ~50ms later on the second route, imperceptible to users.
    import("@/lib/gsap").then(({ gsap }) => {
      gsap.fromTo(
        el,
        { opacity: 0, y: isMobile ? 10 : 20 },
        {
          opacity: 1,
          y: 0,
          duration: isMobile ? 0.3 : 0.5,
          ease: "power3.out",
          clearProps: "all",
        },
      );
    });
  }, [pathname]);

  return (
    <div ref={wrapRef}>
      {children}
    </div>
  );
}
